/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

/**
 * Accumulates performance time marks to be sent in Sandbox done event.
 * @param {string} src Current iframe source URL.
 * @returns {Object} Object containing performance time marks.
 */
export function getPerformanceMarks(src, iframePerformance) {
  let loadTimes = {};
  const navigationEntry = performance.getEntriesByType('navigation')[0] || {};
  const {fetchStart, responseEnd, loadEventStart, domInteractive} = navigationEntry;
  loadTimes.fetchStart = fetchStart || 0;
  loadTimes.responseEnd = responseEnd || 0;
  loadTimes.loadEventStart = loadEventStart || 0;
  loadTimes.domInteractive = domInteractive || 0;

  let performanceEntries = window.performance.getEntries();
  performanceEntries.filter(e => {
    if (/^us/.test(e.name) || e.name.indexOf('paint') > 0 ||
      e.name.indexOf('/token?client_id=exc_app') > 0 || (e.name === src && e.initiatorType === 'iframe')) {
      return true;
    }
    return false;
  }).map(({duration, initiatorType, name, startTime}) => {
    if (initiatorType === 'iframe') {
      name = 'us_iframe_loaded';
    }
    if (name.indexOf('/token?client_id=exc_app') > 0) {
      name = 'us_check_token_start';
      loadTimes['us_check_token_end'] = startTime + duration;
    }
    if (!loadTimes[name]) {
      loadTimes[name] = startTime;
    }
  });
  loadTimes.us_page_done = performance.now();

  const {timeOrigin} = window.performance;
  if (iframePerformance && timeOrigin && iframePerformance.timeOrigin && iframePerformance.loadTimes) {
    /*
      Since the Shell frame and iframe started navigations at different times,
      we need to normalize iframe times by adding the delta:
      iframe navigation time - shell frame navigation time.
     */
    const originDelta = iframePerformance.timeOrigin - timeOrigin;
    for (const timing in iframePerformance.loadTimes) {
      loadTimes[timing] = originDelta + iframePerformance.loadTimes[timing];
    }
  }
  return {loadTimes};
}

/**
 * Returns a landing page redirect based on response from server and current URL
 * @param {Object=} landingPageResponse Response from IMSSSOhandler.
 * @param {string} redirectLocation Where the server believes the user should be redirected.
 * @returns {string} A string of a path, or empty for no-redirect.
 */
export function getLandingPageRedirect(landingPageResponse) {
  let landingPageUrlString = landingPageResponse && landingPageResponse.redirectLocation;
  if (!landingPageUrlString) {
    return '';
  }

  const landingPageUrl = new URL(landingPageUrlString);
  // Identify if the redirect location is a route in exc-app or an external page.
  // Check to be improved further. Currently validating against first level route.
  if (landingPageUrl.origin === window.location.origin) {
    let parts = landingPageUrl.href.split('#');
    return parts.length > 1 ? parts.slice(1).join('#') : '';
  }

  // If user's landing page is ExC Home, take user to Unified Shell Home
  if (landingPageUrl.hostname.startsWith('exc-home') && landingPageUrl.pathname.startsWith('/exc-home/home')) {
    return '/home';
  }

  window.location.assign(landingPageUrl.href);
}

/**
 * Returns true if the navigation was an org change.
 * @param {string} oldPath window location pathname.
 * @param {string} newPath history location pathname.
 * @returns {boolean} Returns true if the navigation was an org change.
 */
export function isOrgChange(oldPath, newPath) {
  let oldPathArr = oldPath.split('/');
  let newPathArr = newPath.split('/');

  // If the length is not the same, more has changed than just the tenant meaning
  // it is not an org change.
  if (oldPathArr.length !== newPathArr.length) {
    return false;
  }
  // Because we are splitting by '/' arr[0] will be "" and arr[1] will be the tenant.
  for (let i = 2; i < oldPathArr.length - 1; i++) {
    if (oldPathArr[i] !== newPathArr[i]) {
      return false;
    }
  }
  return oldPathArr[1] !== newPathArr[1];
}

/**
 * Get name of org.
 * @param {string} org String with org.
 * @param {Array} map List of orgs.
 * @returns {string} Org name or unknown.
 */
export function getOrgName(org, map) {
  let [ident, authSrc] = org.split('@');
  let s = map.find(e => e.orgRef.ident === ident && e.orgRef.authSrc === authSrc);
  return s ? s.orgName : 'unknown';
}
